import { useCallback, useMemo, useState } from 'react'
import { Link } from 'react-router-dom'
import { LockOpen, ShieldCheck } from 'lucide-react'
import { PageHeader } from '@/shell/AppShell'
import { DataTable } from '@/components/DataTable'
import type { AdminColumn, RowAction } from '@/components/DataTable/types'
import { PERMISSIONS } from '@/lib/permissions'
import { useToast } from '@/providers/UiProviders'
import { fetchBanned, unbanUser, type AdminBannedUser } from '@/lib/adapters'
import { BAN_STR as S } from './strikesApi'

/**
 * A8.06 — Cheklovni bekor qilish. JONLI `/admin/banned` ro'yxati; qator amali
 * `/admin/users/:id/unban` (banni bekor qiladi, serverда audit). Backend
 * `e876f10`.
 */
export function RevokeRestrictionScreen() {
  const { toast } = useToast()
  const [reloadKey, setReloadKey] = useState(0)

  const columns = useMemo<AdminColumn<AdminBannedUser>[]>(
    () => [
      { key: 'id', header: 'ID', type: 'id', width: 80 },
      {
        key: 'username',
        header: S.userId,
        type: 'text',
        minWidth: 180,
        sortable: false,
        render: (r) => (
          <Link to={`/users/${r.id}`} className="text-accent hover:underline">
            {r.username ? `@${r.username}` : `#${r.id}`}
          </Link>
        ),
      },
      { key: 'banned_reason', header: S.reason, type: 'longtext', minWidth: 220 },
      { key: 'banned_at', header: S.bannedAt, type: 'date', width: 160 },
    ],
    [],
  )

  const rowActions = useMemo<RowAction<AdminBannedUser>[]>(
    () => [
      {
        id: 'unban',
        label: S.unban,
        icon: <LockOpen size={14} />,
        permission: PERMISSIONS.USERS_BAN,
        onSelect: async (r) => {
          const who = r.username ? `@${r.username}` : `#${r.id}`
          if (!window.confirm(`${S.unbanConfirm} ${who}?`)) return
          try {
            await unbanUser(r.id)
            toast(S.unbanned, { tone: 'success' })
            setReloadKey((k) => k + 1)
          } catch {
            toast(S.failed, { tone: 'error' })
          }
        },
      },
    ],
    [toast],
  )

  const fetcher = useCallback(fetchBanned, [])
  return (
    <>
      <PageHeader title={S.revoke} screenId="A8.06" description={S.revokeNote} />
      <DataTable<AdminBannedUser>
        key={reloadKey}
        screenId="A8.06"
        permission={PERMISSIONS.USERS_VIEW}
        columns={columns}
        fetcher={fetcher}
        rowId={(r) => String(r.id)}
        rowActions={rowActions}
        exportable
        emptyState={{ icon: <ShieldCheck size={28} strokeWidth={1.5} />, title: S.revoke }}
      />
    </>
  )
}
